import { Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { take } from 'rxjs';
import { RETURN_URL_KEY } from './auth.guard';

/**
 * The OIDC redirect target (registered with OpenIddict as {origin}/callback).
 *
 * The code exchange has already been performed by checkAuth() in the app initializer; this route
 * only decides where the user lands, restoring the URL they were originally aiming at so deep links
 * survive login.
 *
 * If we arrive here NOT authenticated, the exchange failed. We must not navigate into a guarded
 * route: the guard would call authorize(), AuthServer would bounce us straight back here (its
 * session cookie is still valid), and we would spin forever. Show the failure instead — a dead end
 * you can read beats an infinite redirect you can't.
 *
 * take(1) matters too: isAuthenticated$ is a live stream, and re-navigating on every emission is
 * another way to build a loop.
 *
 * ## The "which client?" prompt is NOT triggered here — deliberately
 *
 * It was, and it did not fire (Sprints #75). This component only runs when the browser returns from
 * AuthServer carrying an authorization code; a boot that restores an existing session from the
 * refresh token never comes through here at all, so the prompt was reachable only on some sign-ins.
 * It now lives in `clientPromptGuard` on the application shell, which every entry passes through.
 */
@Component({
  selector: 'app-callback',
  standalone: true,
  template: `
    @if (failed()) {
      <div class="mx-auto max-w-lg p-8 text-center">
        <h1 class="mb-2 text-lg font-semibold">Sign-in did not complete</h1>
        <p class="mb-4 text-sm text-gray-600">
          The authorization code could not be exchanged for a token. Check the browser console and
          the network tab for the call to <code>/auth/connect/token</code>.
        </p>
        <button class="text-primary hover:underline" (click)="retry()">Try again</button>
      </div>
    } @else {
      <div class="flex h-screen items-center justify-center">
        <p class="text-gray-500">Signing you in…</p>
      </div>
    }
  `,
})
export class CallbackComponent {
  private readonly oidc = inject(OidcSecurityService);
  private readonly router = inject(Router);

  protected readonly failed = signal(false);

  constructor() {
    this.oidc.isAuthenticated$.pipe(take(1)).subscribe(({ isAuthenticated }) => {
      if (!isAuthenticated) {
        this.failed.set(true);
        return;
      }

      const returnUrl = sessionStorage.getItem(RETURN_URL_KEY);
      sessionStorage.removeItem(RETURN_URL_KEY);
      void this.router.navigateByUrl(returnUrl ?? '/people');
    });
  }

  protected retry(): void {
    this.failed.set(false);
    this.oidc.authorize();
  }
}
